import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import Pagination from "../../Services/Pagination";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import useConfirm from "../../Services/Confirmation";
import { canAccessTakhmeen, canViewAllOrgTakhmeens } from "./index";
import { formatTakhmeenAmount } from "./takhmeenFormat";

const canCreate = (u) => u.role_slug === "super_admin" || u.caps_list.includes("add_takhmeen") || u.caps_list.includes("manage_takhmeen");
const canEdit = (u) => u.role_slug === "super_admin" || u.caps_list.includes("edit_takhmeen") || u.caps_list.includes("manage_takhmeen");

const TakhmeenList = () => {
    const dispatch = useDispatch();
    const customConfirm = useConfirm();
    const { userData, accessToken } = useSelector((s) => s.user);
    const [list, setList] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, per_page: 20 });
    const [orgList, setOrgList] = useState([]);
    const [divList, setDivList] = useState([]);
    const [filter, setFilter] = useState({
        org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
        div_id: 0, status: "", search: "",
    });

    const filteredDivs = useMemo(() => divList.filter((d) => !filter.org_id || parseInt(d.org_id, 10) === parseInt(filter.org_id, 10)), [divList, filter.org_id]);

    const showMembersColumn = useMemo(
        () => list.some((item) => item.list_context !== "member"),
        [list]
    );

    const memberListView = useMemo(
        () => list.length > 0 && list.every((item) => item.list_context === "member"),
        [list]
    );

    const coordinatorListView = useMemo(
        () => list.length > 0 && list.every((item) => item.list_context === "coordinator"),
        [list]
    );

    const tableColSpan = useMemo(() => {
        let cols = 8;
        if (userData.role_slug === "super_admin") cols += 1;
        if (showMembersColumn) cols += 1;
        return cols;
    }, [userData.role_slug, showMembersColumn]);

    const load = (pageNo = page) => {
        dispatch(setMainLoading(true));
        PostAxios("takhmeen/takhmeen-list", { ...filter, page: pageNo, perPage: pagination.per_page }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    setList(data.list || []);
                    setPagination({ total: data.totalRecords || 0, per_page: data.perPage || 20 });
                }
            })
            .catch((e) => responseCatchFunc(dispatch, e))
            .finally(() => dispatch(setMainLoading(false)));
    };

    useEffect(() => {
        dispatch(setPageTitle("Takhmeen"));
        const promises = [];
        if (userData.role_slug === "super_admin") promises.push(PostAxios("masters/organization", { page: 1, perPage: 1000 }, accessToken));
        if (canViewAllOrgTakhmeens(userData)) {
            promises.push(PostAxios("masters/division-list", { org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id, page: 1, perPage: 1000 }, accessToken));
        }
        if (promises.length) {
            Promise.all(promises).then((res) => {
                res.forEach((r) => {
                    if (r.config.url.includes("organization") && r.data.status === "1") setOrgList(r.data.data);
                    if (r.config.url.includes("division-list") && r.data.status === "1") setDivList(r.data.data);
                });
                load(1);
            });
        } else load(1);
    }, []);

    const deleteItem = async (id) => {
        if (!await customConfirm({ description: "Delete this takhmeen?" })) return;
        dispatch(setMainLoading(true));
        PostAxios("takhmeen/delete-takhmeen", { takhmeen_id: id }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") { dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true })); load(page); }
                else dispatch(setToastData({ message: data.message, type: "error", showCloseBtn: true }));
            })
            .finally(() => dispatch(setMainLoading(false)));
    };

    if (!canAccessTakhmeen(userData)) return null;

    return (
        <div className="main-form-sec">
            {canCreate(userData) && (
                <div className="top-right-btn p-0">
                    <Link to="/takhmeen/add" className="btn-blue btn br-0">Add Takhmeen</Link>
                </div>
            )}
            <div className="top-reports-filter">
                <div className="right-filter-sec">
                    <div className="p-0 filter-search form-field-group report-filter d-flex flex-wrap gap-2 align-items-center">
                        {userData.role_slug === "super_admin" && (
                            <div className="report-filter-field align-items-center">
                                <label className="select-input-outer">Organization</label>
                                <div className="static-items-outer">
                                    <select className="select-input" value={filter.org_id} onChange={(e) => setFilter({ ...filter, org_id: e.target.value, div_id: 0 })}>
                                        <option value={0}>All</option>
                                        {orgList.map((o) => <option key={o.ID} value={o.ID}>{o.short_name}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}
                        {canViewAllOrgTakhmeens(userData) && (
                            <div className="report-filter-field align-items-center">
                                <label className="select-input-outer">Division</label>
                                <div className="static-items-outer">
                                    <select className="select-input" value={filter.div_id} onChange={(e) => setFilter({ ...filter, div_id: e.target.value })}>
                                        <option value={0}>All</option>
                                        {filteredDivs.map((d) => <option key={d.ID} value={d.ID}>{d.division_name}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="report-filter-field align-items-center">
                            <label className="select-input-outer">Search</label>
                            <div className="static-items-outer">
                                <input className="form-input" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
                            </div>
                        </div>
                        <button type="button" className="btn btn-blue" onClick={() => { setPage(1); load(1); }}>Submit</button>
                    </div>
                </div>
            </div>
            <div className="table-main">
                <table className="table">
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            {userData.role_slug === "super_admin" && <th>Org</th>}
                            <th>Name</th>
                            <th>Division</th>
                            <th>{memberListView ? "Takhmeen Amount" : coordinatorListView ? "Assigned Amount" : "Amount"}</th>
                            <th>{memberListView || coordinatorListView ? "Amount Paid" : "Collected"}</th>
                            {showMembersColumn && <th>Members</th>}
                            <th>End Date</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!list.length && <tr><td colSpan={tableColSpan} className="text-center text-muted py-4">No takhmeen found.</td></tr>}
                        {list.map((item, i) => (
                            <tr key={item.id}>
                                <td>{(page - 1) * pagination.per_page + i + 1}</td>
                                {userData.role_slug === "super_admin" && <td>{item.org_name}</td>}
                                <td>{item.takhmeen_name}</td>
                                <td>{item.division_name} - {item.division_number}</td>
                                <td>{formatTakhmeenAmount(item.display_amount ?? item.base_amount)}</td>
                                <td>{formatTakhmeenAmount(item.display_collected ?? item.total_collected ?? 0)}</td>
                                {showMembersColumn && (
                                    <td>{item.list_context === "member" ? "-" : (item.display_member_count ?? item.member_count)}</td>
                                )}
                                <td>{moment(item.end_date).format("DD MMM YYYY")}</td>
                                <td>{parseInt(item.status, 10) === 1 ? "Active" : "Inactive"}</td>
                                <td>
                                    <Link to="/takhmeen/view" state={{ takhmeen_id: item.id }} className="btn btn-sm btn-blue me-1">View</Link>
                                    {canEdit(userData) && (
                                        <>
                                            <Link to="/takhmeen/edit" state={{ takhmeen_id: item.id }} className="btn btn-sm btn-blue me-1">Edit</Link>
                                            <button type="button" className="btn btn-sm status-btn rejected" style={{ float: "none" }} onClick={() => deleteItem(item.id)}>Delete</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination currentPage={page} totalCount={pagination.total} pageSize={pagination.per_page} onPageChange={(p) => { setPage(p); load(p); }} />
        </div>
    );
};

export default TakhmeenList;
